import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Chip,
  Paper,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { ROLES, PERMISSIONS } from '../../api/endpoints';
import { extractList } from '../../utils/response';
import { toast } from 'react-toastify';
import { buildRolePayload } from './roleApiHelpers';

const DEFAULT_GUARD_NAME = 'user';

const groupPermissionsByCategory = (permissions, searchText) => {
  const filtered = permissions.filter((p) =>
    (p.name || '').toLowerCase().includes(searchText.toLowerCase())
  );
  const categories = {};
  filtered.forEach((permission) => {
    const category = (permission.name || '').split('-')[0] || 'general';
    if (!categories[category]) categories[category] = [];
    categories[category].push(permission);
  });
  return categories;
};

const CreateRole = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [loadingPage, setLoadingPage] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const [name, setName] = useState('');
  const [guardName, setGuardName] = useState(DEFAULT_GUARD_NAME);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [searchText, setSearchText] = useState('');

  const fetchAllPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const { data } = await api.get(PERMISSIONS.LIST);
      const list = extractList(data);
      setAllPermissions(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Could not load permissions');
      setAllPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllPermissions();
  }, [fetchAllPermissions]);

  useEffect(() => {
    if (!isEdit || !editId) {
      setLoadingPage(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPage(true);
      try {
        const { data } = await api.get(ROLES.BY_ID(editId));
        const role = data?.data ?? data;
        if (cancelled || !role) return;

        setName(role.name ?? '');
        setGuardName(role.guard_name ?? DEFAULT_GUARD_NAME);

        const rolePermissions = role.permissions ?? data?.data?.permissions ?? [];
        const ids = Array.isArray(rolePermissions)
          ? rolePermissions.map((p) => (typeof p === 'object' ? p.id : p)).filter(Boolean)
          : [];
        setSelectedPermissionIds(ids);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load role');
        navigate('/roles');
      } finally {
        if (!cancelled) setLoadingPage(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, navigate]);

  const permissionCategories = useMemo(
    () => groupPermissionsByCategory(allPermissions, searchText),
    [allPermissions, searchText]
  );

  const handlePermissionChange = (permissionId) => (event) => {
    if (event.target.checked) {
      setSelectedPermissionIds((prev) => [...prev, permissionId]);
    } else {
      setSelectedPermissionIds((prev) => prev.filter((id) => id !== permissionId));
    }
  };

  const getCategoryCheckState = (categoryPermissions) => {
    const ids = categoryPermissions.map((p) => p.id);
    const selectedCount = ids.filter((id) => selectedPermissionIds.includes(id)).length;
    return {
      ids,
      allSelected: ids.length > 0 && selectedCount === ids.length,
      indeterminate: selectedCount > 0 && selectedCount < ids.length,
    };
  };

  const handleCategorySelectAll = (categoryPermissions) => (event) => {
    event.stopPropagation();
    const { ids, allSelected } = getCategoryCheckState(categoryPermissions);
    if (allSelected) {
      setSelectedPermissionIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedPermissionIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.warn('Enter a role name');
      return;
    }
    if (selectedPermissionIds.length === 0) {
      toast.warn('Select at least one permission');
      return;
    }

    const payload = buildRolePayload({
      name,
      guardName: guardName.trim() || DEFAULT_GUARD_NAME,
      permissionIds: selectedPermissionIds,
    });

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(ROLES.BY_ID(editId), payload);
        toast.success('Role updated');
      } else {
        await api.post(ROLES.LIST, payload);
        toast.success('Role created');
      }
      navigate('/roles');
    } catch (err) {
      toast.error(
        err.response?.data?.message || (isEdit ? 'Failed to update role' : 'Failed to create role')
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/roles')} sx={{ mb: 2 }}>
          Back to roles
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit role' : 'Create role'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Role name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                  placeholder="e.g. Operations User"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon color="primary" />
              <Typography variant="h6">Permissions</Typography>
              <Chip label={`${selectedPermissionIds.length} selected`} size="small" color="primary" variant="outlined" />
            </Box>

            <TextField
              fullWidth
              size="small"
              placeholder="Search permissions..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {permissionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ maxHeight: 420, overflow: 'auto', mb: 2 }}>
                {Object.keys(permissionCategories).length > 0 ? (
                  Object.keys(permissionCategories).map((category) => {
                    const categoryPermissions = permissionCategories[category];
                    const { allSelected, indeterminate } = getCategoryCheckState(categoryPermissions);

                    return (
                    <Accordion key={category} defaultExpanded={Object.keys(permissionCategories).length <= 4}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Checkbox
                            checked={allSelected}
                            indeterminate={indeterminate}
                            onChange={handleCategorySelectAll(categoryPermissions)}
                            onClick={(e) => e.stopPropagation()}
                            color="primary"
                            size="small"
                            inputProps={{ 'aria-label': `Select all ${category} permissions` }}
                          />
                          <CategoryIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle2" fontWeight={600}>
                            {category.toUpperCase()}
                          </Typography>
                          <Chip label={categoryPermissions.length} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', mr: 1 }}>
                            Select all
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={0.5}>
                          {categoryPermissions.map((permission) => (
                            <Grid item xs={12} sm={6} key={permission.id}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedPermissionIds.includes(permission.id)}
                                    onChange={handlePermissionChange(permission.id)}
                                    color="primary"
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                                    {permission.name}
                                  </Typography>
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    {allPermissions.length === 0 ? 'No permissions available' : 'No permissions match your search'}
                  </Typography>
                )}
              </Box>
            )}

            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 1.5,
                mb: 2,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              })}
            >
              <CheckCircleIcon fontSize="small" />
              <Typography variant="body2">
                {selectedPermissionIds.length} permission(s) selected
              </Typography>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" disabled={submitting || permissionsLoading}>
                {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Create role'}
              </Button>
              <Button type="button" variant="outlined" onClick={() => navigate('/roles')} disabled={submitting}>
                Cancel
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateRole;
